import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {Select, Store} from '@ngxs/store';
import { Deal } from 'app/models/dto/deal.model';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { ApplicationState } from 'app/store/deal/application/application.state';
import {LenderUserState} from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import {Observable} from 'rxjs';
import * as ApprovalCalculatorUtility from '@lendiodevs/lendio-js-utilities/lib/approval/ApprovalCalculator.utility.js';
import { Calculator, configureCalculator } from '@lendiodevs/lendio-js-utilities';
import { AchApprovalOption } from 'app/models/dto/approval/ach-approval-option.model';
import { toUnredirectedSourceFile } from '@angular/compiler-cli/src/ngtsc/util/src/typescript';

@Component({
  selector: 'app-deals-table',
  templateUrl: './deals-table.component.html',
  styleUrls: ['./deals-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class DealsTableComponent implements OnChanges, OnInit {

  @Input() deals: Deal[];
  @Input() borrowerId: number;
  @Input() history = false;
  @Input() loading = false;
  @Input() bulkSubmitting = false;
  @Input() readOnly = false;
  @Input() showDealRightColumn: boolean = true;
  @Input() showDealsPageLink: boolean = false;
  @Input() activeColumns = [
    'expand',
    'lenderProduct',
    'offerAmount',
    'offerPayback',
    'offerRate',
    'offerTerm',
    'offerFrequency',
    'offerPayment',
    'offerFeesPerDollar',
    'offerPoints',
    'status'
  ];
  @Output() submit = new EventEmitter<boolean>();
  @Select(LenderUserState.hasPermission('canSeeDealStatusRestrictions')) canSeeDealStatusRestrictions$: Observable<boolean>;

  dataSource: MatTableDataSource<Deal>;
  columnsToDisplay: string[];
  currencySymbol: string | null;
  historyColumns = [
    'expand',
    'lenderProduct',
    'lastApplicationDate',
    'status',
    'reason',
    'comment',
    'renewal'
  ];
  expandedDeals: Deal[] = [];

  @ViewChild(MatSort) set content(sort: MatSort) {
    this.dataSource.sortingDataAccessor = (deal, property) => {
      switch (property) {
        case 'lenderProduct':
        case 'lender':
          return deal.loan_product_lender.name;
        case 'loanProduct':
          return deal.loan_product.category.shortName;
        case 'offerAmount':
          return deal.highestOffer ? deal.highestOffer.amount : null;
        case 'offerPayback':
          return deal.highestOffer ? deal.highestOffer.payback : null;
        case 'offerRate':
          if (deal.highestOffer) {
            return deal.highestOffer.apr
              ? deal.highestOffer.apr : deal.highestOffer.factor
              ? deal.highestOffer.factor : deal.highestOffer.interestRate
              ? deal.highestOffer.interestRate : null;
          }
          return null;
        case 'offerTerm':
          return deal.highestOffer ? deal.highestOffer.term : null;
        case 'offerFrequency':
          return deal.highestOffer ? deal.highestOffer.paymentFrequency : null;
        case 'offerPayment':
          return deal.highestOffer ? deal.highestOffer.paymentAmount : null;
        case 'offerFeesPerDollar':
          return deal.highestOffer && deal.highestOffer.calculatedFields ? deal.highestOffer.calculatedFields.centsOnDollar : null;
        case 'offerPoints':
          return deal.highestOffer ? deal.highestOffer.points : null;
        default: return deal[property];
      }
    };
    this.dataSource.sort = sort;
  }

  constructor(
    private store: Store,
    private localeSettingService: LocaleSettingService
  ) { }

  ngOnInit(): void {
    this.columnsToDisplay = !this.history ? this.activeColumns : this.historyColumns;
    this.currencySymbol = this.localeSettingService.getLocaleCurrencySymbol();
    const savedExpandedDeals = this.savedExpandedDeals()[`${this.borrowerId}`];
    if (savedExpandedDeals) {
      this.deals.forEach(deal => {
        if (savedExpandedDeals.find((id: number) => id === deal.id)) {
          this.expandedDeals.push(deal);
        }

        if(this.readOnly) {
          const statusOption = deal.statusOptions.filter( opt => opt.value === deal.status)[0];
          deal.statusDisplay = statusOption?.display || '';
        }
        if(deal.highestApproval){
          deal['approvalMaxVals'] = this.calculateApprovalMaxValues(deal.highestApproval);
          //floor currency values
          deal['approvalMaxVals']['amount'] = Math.floor(deal['approvalMaxVals']['amount']);
          deal['approvalMaxVals']['payback'] = Math.floor(deal['approvalMaxVals']['payback']);
          deal['approvalMaxVals']['payment'] = Math.floor(deal['approvalMaxVals']['payment']);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deals) {
      const dealsData = this.deals.map( deal => {
        deal.applications.forEach( app => {
          if( !deal.lastApplicationDate || app.created > deal.lastApplicationDate ) {
            deal.lastApplicationDate = app.created;
          }
        });

        return deal;
      });
      this.dataSource = new MatTableDataSource(dealsData.sort(this.compareDealsOnStatus.bind(this)));
    }
  }

  sortDeals(sort: Sort) {
    if (sort.direction !== '') {
      return;
    }
    const data = this.dataSource.data ? this.dataSource.data.slice() : [];
    const sortedData = data.sort(this.compareDealsOnStatus);
    this.dataSource.data = sortedData;
  }

  compareDealsOnStatus(a: Deal, b: Deal) {
    const comparator = this.history ? 'lastApplicationDate' : 'modifiedTimestamp';
    const aVal = a[comparator] || '', bVal = b[comparator] || '';
    if (a.stage === b.stage) {
      // a and b are both in same stage, now sort by modifiedTimestamp
      if (aVal === bVal) {
        return 0;
      } else if (aVal < bVal) {
        return 1;
      } else {
        return -1;
      }
    } else if (a.stage === 'funded') {
      // b is not funded
      return -1;
    } else {
      // b is funded
      return 1;
    }
  }

  getDealInactiveReason(deal: Deal) {
    return deal.last_inactive_status && deal.last_inactive_status.reason ? deal.last_inactive_status.reason.split(', ')[0] : '—';
  }

  getAdditionalInactiveReasons(deal: Deal) {
    const additionalReasons = deal.last_inactive_status && deal.last_inactive_status.reason
      ? deal.last_inactive_status.reason.split(', ')
      : [];

    if (deal.dead_disposition_deals) {
      deal.dead_disposition_deals.forEach( (deadDeal) => {
        if (deadDeal.lenderReason) {
          additionalReasons.push(deadDeal.lenderReason);
        }
      });
    }

    if (additionalReasons.length > 1) {
      additionalReasons.shift();
      return additionalReasons.join(', ');
    }

    return 'No additional reasons provided';
  }

  getDealCommentTooltip(deal: Deal) {
    return deal.last_inactive_status && deal.last_inactive_status.otherReason ? deal.last_inactive_status.otherReason : 'No lender notes';
  }

  dealTrackBy(index: number, deal: Deal) {
    return deal.id;
  }

  toggleExpanded(deal: Deal) {
    const savedExpandedDeals = this.savedExpandedDeals();
    if (this.dealExpanded(deal)) {
      this.expandedDeals = this.expandedDeals.filter(d => d.id !== deal.id);
      savedExpandedDeals[this.borrowerId] = savedExpandedDeals[this.borrowerId].filter(id => id !== deal.id);
    } else {
      this.expandedDeals.push(deal);
      if (savedExpandedDeals[this.borrowerId]) {
        savedExpandedDeals[this.borrowerId] = [ ...savedExpandedDeals[this.borrowerId], deal.id ];
      } else {
        savedExpandedDeals[this.borrowerId] = [deal.id];
      }
    }
    localStorage.setItem('expandedDeals', JSON.stringify(savedExpandedDeals));
  }

  dealExpanded(deal: Deal) {
    return this.expandedDeals.filter(d => d.id === deal.id).length > 0 ? true : false;
  }

  calculateApprovalMaxValues(approval){
    //Approval Vars
    const options: AchApprovalOption[] = approval.options;

    const FREQ_MAP = {
      'daily': 1,
      'weekly': 7,
      'semiMonthly': 14,
      'monthly': 30,
    };

    const { buyRate = undefined } = approval.calculatorConfig || {};
    // configure calculator
    const calculator: Calculator = configureCalculator({ buyRate });

    const maxTerm = options.reduce((max, o) => o.term > max ? o.term : max, 0);
    const minTerm = options.reduce((min, o) => o.term < min ? o.term : min, 9999999999999);

    const maxPeriod = options
      .filter(option => option.term === maxTerm)
      .reduce((max, o) =>
        FREQ_MAP[o.paymentFrequency] > FREQ_MAP[max] ? o.paymentFrequency : max, 'daily'
      );

    const maxOption = options.find(o => o.term === maxTerm && o.paymentFrequency === maxPeriod);
    if (!maxOption) {
      return;
    }
    const maxOptionTerm = maxOption.term;
    const maxOptionFreq = maxOption.paymentFrequency;
    const maxOptionMaxAmount = maxOption.limits.amount.max;
    const maxOptionMaxPoints = maxOption.limits.points.max;
    const explicitOriginationMax = maxOption.limits.originationAmount?.max;
    const maxOriginationPercent = maxOption.limits.originationPercent?.max ?
                                  maxOption.limits.originationPercent?.max :
                                  explicitOriginationMax ?
                                  this.toFixedNumber(explicitOriginationMax / maxOptionMaxAmount, 2, 10) :
                                  0;


    const maxBuyrateConditional = maxOption.customCalculatorParams?.pointConditionalBuyrateThreshold;
    const maxBuyRate = (maxOption !== undefined && maxOptionMaxPoints !== undefined) ?
      calculator.buyRate(maxOption.buyRate, maxOptionMaxPoints, {pointConditionalBuyrateThreshold: maxBuyrateConditional}) : 0;

    const calculationType = approval.calculationType;
    const roundingStrategy = approval.loanProductRoundingStrategy;

    const pointsTradable = approval.pointsTradable;
    const originationPercent = approval.originationPercent;
    const factor = ApprovalCalculatorUtility.getFactorRate({
      pointsTradable: pointsTradable,
      buyRate: maxBuyRate,
      points: maxOptionMaxPoints,
      maxOriginationPercent: maxOriginationPercent,
      originationPercent: originationPercent,
    });

    const maxCalculatedOptionAmount =
    ApprovalCalculatorUtility.getOptionAvailableAmount({option: maxOption, calculationType, factor: factor, roundingStrategy});

    const amount = maxCalculatedOptionAmount;

    const payback = ApprovalCalculatorUtility.getPaybackAmount({
      factor: factor,
      amount: amount,
    });

    const maxOptionPayback  = payback;
    const maxOptionFactor = factor;

    const centsOnDollar = maxOptionPayback / Number(maxCalculatedOptionAmount) - 1;

    const payments = ApprovalCalculatorUtility.getPaymentsNumber(maxOption.payments);
    const maxPayment = ApprovalCalculatorUtility.getPaymentAmount({
      payback: maxOptionPayback,
      payments: payments,
    });

    const _maxOption = {
      amount: maxCalculatedOptionAmount,
      payback: maxOptionPayback,
      factor: maxOptionFactor,
      term: maxOptionTerm,
      frequency: maxOptionFreq,
      payment: maxPayment,
      fee: centsOnDollar,
      points: maxOptionMaxPoints,
    };
    return _maxOption;
  }

  toFixedNumber(num: number, digits: number, base: number) {
    const pow = Math.pow(base || 10, digits);
    return Math.round(num * pow) / pow;
  }

  showApprovalData(deal){
    if(deal.stage === 'application'){
      return true;
    } else if(deal.stage === 'underwriting' && deal.status !== 'offerAccepted'){
      return true;
    } else {
      return false;
    }
  }

  private savedExpandedDeals(): { [key: number]: number[] } {
    return JSON.parse(localStorage.getItem('expandedDeals') || '{}');
  }

}
